import * as React from "react";

// Short, plain-English reading of the face verification settings being edited,
// shown beside the form. Reads the live form values (seconds, as strings), so it
// updates as the admin types.

// Returns a non-negative number, or null when the field is blank/invalid.
const num = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const sec = (v) => {
  const n = num(v);
  return n === null ? "?s" : `${n}s`;
};

// "1h 30m", "45s"; null when the wait is 0.
const waitText = (form) => {
  const h = num(form.cooldownHours) || 0;
  const m = num(form.cooldownMinutes) || 0;
  const s = num(form.cooldownSeconds) || 0;
  if (h + m + s === 0) return null;
  return [h && `${h}h`, m && `${m}m`, s && `${s}s`].filter(Boolean).join(" ");
};

// One short line per check, plus its worst-case number of tries (null when it
// is continuous, off or incomplete).
const describeApi = (a) => {
  if (!a.enabled) return { text: "off", off: true, maxTries: null };

  if (a.mode === "continuous") {
    return {
      text: `every ${sec(a.successGapSec)} (${sec(a.failGapSec)} after a fail), never ends journey`,
      maxTries: null,
    };
  }

  const calls = num(a.callsPerAttempt);
  const rounds = num(a.maxAttempts);
  if (!calls || !rounds) return { text: "incomplete", maxTries: null };

  const tries = `${calls} ${calls === 1 ? "try" : "tries"}`;
  const text =
    rounds === 1
      ? `${tries}, ${sec(a.gapSec)} apart, then journey ends`
      : `${tries} × ${rounds} rounds, ${sec(a.gapSec)} apart, then journey ends`;
  return { text, maxTries: calls * rounds };
};

export default function VerificationSummary({ form, pages, apis }) {
  if (!form) return null;
  const wait = waitText(form);

  return (
    <div className="border-2 border-gray-300 flex flex-col">
      <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
        Summary
      </p>
      <div className="px-3 py-3 text-sm text-black-150 flex flex-col gap-3">
        <div className="text-gray-600">
          <p className="mb-0">
            Order: {apis.map((a) => a.label).join(" → ")} · network errors
            don&apos;t count
          </p>
          <p className="mb-0">
            Retry after failure: {wait ? `after ${wait}` : "immediately"}
          </p>
        </div>

        {pages.map(({ key: page, label: pageLabel, hiddenApis = [] }) => {
          const pageForm = form.pages[page];
          const lines = apis.filter(({ key: api }) => !hiddenApis.includes(api)).map(({ key: api, label }) => ({
            api,
            label,
            ...describeApi(pageForm[api]),
          }));
          const anyOn = lines.some((l) => !l.off);
          const maxTries = lines.reduce((sum, l) => sum + (l.maxTries || 0), 0);
          const heading = [
            pageLabel,
            anyOn ? `starts after ${sec(pageForm.initialDelaySec)}` : "no checks",
            maxTries ? `max ${maxTries} tries` : null,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <div key={page} className="flex flex-col gap-0.5">
              <p className="font-semibold mb-0">{heading}</p>
              {lines.map((l) => (
                <p
                  key={l.api}
                  className={`mb-0 ${l.off ? "text-gray-400" : ""}`}
                >
                  {l.label} — {l.text}
                </p>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
